import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import PermissionStep from './PermissionStep';
import SimulationReviewStep from './SimulationReviewStep';
import PersonalDataStep from './PersonalDataStep';
import AdditionalContactsStep from './AdditionalContactsStep';
import DocumentsStep from './DocumentsStep';
import ConfirmationStep from './ConfirmationStep';
import type { RegistrationData, RegistrationLink } from './types';
import { emptyAddress, emptyContact, DOCUMENT_CATEGORIES } from './types';
import { CircleCheck as CheckCircle, Loader, CircleAlert as AlertCircle } from 'lucide-react';

const STEPS = [
  { id: 1, label: 'Permissões' },
  { id: 2, label: 'Empréstimo' },
  { id: 3, label: 'Dados' },
  { id: 4, label: 'Contatos' },
  { id: 5, label: 'Documentos' },
  { id: 6, label: 'Revisão' },
];

function initRegistrationData(): RegistrationData {
  return {
    selfieUrl: null,
    latitude: null,
    longitude: null,
    locationAccuracy: null,
    locationCapturedAt: null,
    deviceInfo: '',
    permissionCamera: false,
    permissionLocation: false,
    name: '',
    cpf: '',
    phone: '',
    enderecoResidencial: { ...emptyAddress },
    enderecoComercial: { ...emptyAddress },
    pixKey: '',
    bank: '',
    serviceEntryTime: '',
    serviceExitTime: '',
    contacts: [emptyContact(), emptyContact(), emptyContact(), emptyContact()],
    documents: DOCUMENT_CATEGORIES.map(cat => ({ ...cat, file: null, url: null, uploading: false })),
    declarationConfirmed: false,
  };
}

type PageState = 'loading' | 'invalid' | 'active' | 'success';

export default function ClientRegistration() {
  const { slug } = useParams<{ slug: string }>();

  const [pageState, setPageState] = useState<PageState>('loading');
  const [invalidReason, setInvalidReason] = useState('');
  const [link, setLink] = useState<RegistrationLink | null>(null);
  const [registrationId, setRegistrationId] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [data, setData] = useState<RegistrationData>(initRegistrationData);

  useEffect(() => {
    if (!slug) {
      setInvalidReason('Link inválido.');
      setPageState('invalid');
      return;
    }
    loadLink();
  }, [slug]);

  const loadLink = async () => {
    try {
      const { data: linkData, error } = await supabase
        .from('seller_registration_links')
        .select('*')
        .eq('slug', slug)
        .maybeSingle();

      if (error) throw error;

      if (!linkData) {
        setInvalidReason('Este link não existe ou foi removido.');
        setPageState('invalid');
        return;
      }

      if (!linkData.is_active || linkData.status === 'cancelled') {
        setInvalidReason('Este link foi cancelado e não está mais disponível.');
        setPageState('invalid');
        return;
      }

      if (linkData.status === 'completed') {
        setInvalidReason('Este link já foi utilizado e o cadastro foi concluído.');
        setPageState('invalid');
        return;
      }

      if (linkData.status === 'expired') {
        setInvalidReason('Este link expirou.');
        setPageState('invalid');
        return;
      }

      setLink(linkData as RegistrationLink);

      // Check for existing registration (to resume)
      const { data: existing } = await supabase
        .from('client_self_registrations')
        .select('id')
        .eq('link_id', linkData.id)
        .neq('status', 'completed')
        .maybeSingle();

      if (existing) {
        setRegistrationId(existing.id);
      } else {
        // Create new registration record
        const { data: newReg, error: regError } = await supabase
          .from('client_self_registrations')
          .insert({
            link_id: linkData.id,
            seller_id: linkData.seller_id,
            unit_id: linkData.unit_id,
            status: 'pending',
          })
          .select('id')
          .single();

        if (regError) throw regError;
        setRegistrationId(newReg.id);
      }

      // Mark link as opened if still pending
      if (linkData.status === 'pending') {
        await supabase
          .from('seller_registration_links')
          .update({ status: 'opened', opened_at: new Date().toISOString() })
          .eq('id', linkData.id);
      }

      setPageState('active');
    } catch (err) {
      console.error('Error loading link:', err);
      setInvalidReason('Erro ao carregar o link. Verifique sua conexão e tente novamente.');
      setPageState('invalid');
    }
  };

  const updateData = (partial: Partial<RegistrationData>) => {
    setData(prev => ({ ...prev, ...partial }));
  };

  const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, STEPS.length));
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-10 h-10 text-blue-500 animate-spin mx-auto mb-3" />
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  if (pageState === 'invalid') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-sm w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Link Indisponível</h1>
          <p className="text-gray-600">{invalidReason}</p>
        </div>
      </div>
    );
  }

  if (pageState === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-sm w-full text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">Cadastro Concluído!</h1>
          <p className="text-gray-600 mb-6">
            Suas informações foram enviadas com sucesso. A vendedora será notificada e entrará em contato em breve.
          </p>
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
            <p className="text-sm text-emerald-800">
              Guarde o contato da vendedora para acompanhar o andamento da sua proposta.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-lg mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Cadastro de Empréstimo</p>
              <p className="text-sm font-semibold text-gray-900">Etapa {currentStep} de {STEPS.length}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">{STEPS[currentStep - 1].label}</p>
            </div>
          </div>

          {/* Step dots */}
          <div className="flex items-center gap-1">
            {STEPS.map((step) => (
              <div
                key={step.id}
                className={`flex-1 h-1.5 rounded-full transition-all duration-300 ${
                  step.id < currentStep ? 'bg-emerald-500' :
                  step.id === currentStep ? 'bg-blue-500' :
                  'bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-lg mx-auto px-4 py-6 pb-24">
        {currentStep === 1 && registrationId && (
          <PermissionStep
            slug={slug!}
            registrationId={registrationId}
            onComplete={(partial) => {
              updateData(partial);
              nextStep();
            }}
          />
        )}

        {currentStep === 2 && link && (
          <SimulationReviewStep
            link={link}
            onConfirm={nextStep}
          />
        )}

        {currentStep === 3 && registrationId && (
          <PersonalDataStep
            data={data}
            registrationId={registrationId}
            onComplete={(partial) => {
              updateData(partial);
              nextStep();
            }}
          />
        )}

        {currentStep === 4 && registrationId && (
          <AdditionalContactsStep
            data={data}
            registrationId={registrationId}
            slug={slug!}
            onComplete={(partial) => {
              updateData(partial);
              nextStep();
            }}
          />
        )}

        {currentStep === 5 && registrationId && (
          <DocumentsStep
            data={data}
            registrationId={registrationId}
            slug={slug!}
            onComplete={(partial) => {
              updateData(partial);
              nextStep();
            }}
          />
        )}

        {currentStep === 6 && link && registrationId && (
          <ConfirmationStep
            data={data}
            link={link}
            registrationId={registrationId}
            onComplete={() => setPageState('success')}
          />
        )}
      </div>

      {/* Back button (floating, only when not step 1) */}
      {currentStep > 1 && currentStep < 6 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-4 py-3">
          <div className="max-w-lg mx-auto">
            <button
              onClick={prevStep}
              className="w-full py-3 border-2 border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors text-sm"
            >
              ← Etapa anterior
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
